import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import {
  Button,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { PDFDownloadLink } from "@react-pdf/renderer";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import appcolors from "../../constants/Colors";
import { CURRENCY } from "../../constants/Variables";
import {
  calculateProductOrderVatAmount,
  calculateSupplierTotalOrderPrice,
  calculateVat,
  roundTo2Decimal,
} from "../../utils";
import EcommerceInvoicePDF from "../InvoiceTemplate/Ecommerce";

const tableStyle = {
  "&.MuiTableContainer-root .MuiTableHead-root": {
    backgroundColor: appcolors.primary,
  },
  "&.MuiTableContainer-root .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root":
    {
      padding: "6px 16px",
      color: appcolors.white,
      fontSize: "1rem",
    },
  "& .view_btn": {
    border: "1px solid grey",
    height: 35,
    width: 75,
    fontSize: "0.875rem",
    borderRadius: "6px",
  },
};

export default function OrderTable({
  tableWidth,
  minWidth,
  tableHeader,
  tableData,
  displayRows,
  handleViewDetail,
  showDownloadBtn,
  onPageChange,
  onRowsPerPageChange,
  paginatedData,
  isLoading = false,
}) {
  const { t } = useTranslation(["common"]);
  const { currentLanguage } = useSelector((state) => state.languageReducer);
  const skeletonRowCount = paginatedData?.limit || 10;

  return (
    <React.Fragment key={"order-table"}>
      <TableContainer sx={tableStyle}>
        <Table
          sx={{
            width: tableWidth ? tableWidth : "100%",
            minWidth: minWidth ? minWidth : "900px",
          }}
        >
          <TableHead>
            <TableRow>
              {tableHeader?.map((header) => (
                <TableCell key={header}>{t(header)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
                <TableRow key={`skeleton-row-${rowIndex}`}>
                  {displayRows?.map((val, cellIndex) => (
                    <TableCell key={`${val}-${cellIndex}`}>
                      <Skeleton animation="wave" height={24} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : tableData?.length === 0 ? (
              <TableRow>
                <TableCell align="center" colSpan={tableHeader?.length || 1}>
                  <Typography fontWeight={500}>{t("noDataFound")}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              tableData?.map((row, index) => {
                const discountAmount = row?.discount?.amount || 0;
                const discountVat = calculateVat(discountAmount, row?.vat);

                const orderSubTotal =
                  calculateSupplierTotalOrderPrice(row?.order) - discountAmount;

                const totalVatAmount = roundTo2Decimal(
                  calculateProductOrderVatAmount(row?.order) - discountVat,
                );

                const finalOrderTotal = roundTo2Decimal(
                  orderSubTotal +
                    row?.platformFee +
                    row?.shippingFee +
                    totalVatAmount,
                );

                return (
                  <TableRow key={index}>
                    {displayRows.map((val) => (
                      <React.Fragment key={val}>
                        {val === "created_date" ? (
                          <TableCell>
                            {moment(row.createdAt).format("DD/MM/YYYY")}
                            <br />
                            {moment(row.createdAt).format("hh:mm:ss a")}
                          </TableCell>
                        ) : val === "order_id" ? (
                          <TableCell>
                            <Typography variant={"body2"} fontWeight={500}>
                              {row?.orderId}
                            </Typography>
                          </TableCell>
                        ) : val === "user_detail" ? (
                          <TableCell>
                            <Typography variant={"body2"} fontWeight={500}>
                              {`${row?.user?.firstname} ${row?.user?.lastname}`}
                            </Typography>
                            <Typography variant={"body2"} fontWeight={400}>
                              {row?.user?.email}
                            </Typography>
                            <Typography variant={"body2"} fontWeight={400}>
                              {row?.user?.contact}
                            </Typography>
                          </TableCell>
                        ) : val === "download" ? (
                          <TableCell>
                            {showDownloadBtn && (
                              <Stack
                                direction="row"
                                my={2}
                                justifyContent={"center"}
                              >
                                <PDFDownloadLink
                                  document={
                                    <EcommerceInvoicePDF
                                      invoiceData={row}
                                      currentLanguage={currentLanguage}
                                    />
                                  }
                                  fileName={`invoice-${row?.orderId}.pdf`}
                                >
                                  {({ blob, url, loading, error }) => (
                                    <Button
                                      variant="contained"
                                      disabled={loading}
                                      sx={{
                                        width: "auto",
                                        height: "30px",
                                        borderRadius: "6px",
                                      }}
                                      startIcon={<FileDownloadIcon />}
                                    >
                                      {loading ? t("generating") : t("invoice")}
                                    </Button>
                                  )}
                                </PDFDownloadLink>
                              </Stack>
                            )}
                          </TableCell>
                        ) : val === "user_contact" ? (
                          <TableCell>{row?.userId.contact}</TableCell>
                        ) : val === "shipping_fee" ? (
                          <TableCell>
                            {`${row.shippingFee} ${CURRENCY}`}
                          </TableCell>
                        ) : val === "order_tax" ? (
                          <TableCell>{row?.vat}%</TableCell>
                        ) : val === "order_payment_type" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {row?.paymentMethod}
                          </TableCell>
                        ) : val === "order_delivery_type" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {row?.deliveryType}
                          </TableCell>
                        ) : val === "order_platform_fee" ? (
                          <TableCell>{`${row.platformFee} ${CURRENCY}`}</TableCell>
                        ) : val === "order_supplier_price" ? (
                          <TableCell>{`${orderSubTotal} ${CURRENCY}`}</TableCell>
                        ) : val === "order_total_bill" ? (
                          <TableCell>{`${finalOrderTotal} ${CURRENCY}`}</TableCell>
                        ) : val === "order_vat_amount" ? (
                          <TableCell>{`${totalVatAmount} ${CURRENCY}`}</TableCell>
                        ) : val === "order_detail" ? (
                          <TableCell>
                            <Chip
                              icon={<RemoveRedEyeIcon />}
                              label={t("view")}
                              onClick={() => handleViewDetail(row, index)}
                            />
                          </TableCell>
                        ) : (
                          <TableCell>
                            <Typography
                              lineHeight="normal"
                              fontSize={"0.875rem"}
                            >
                              {row[val]}
                            </Typography>
                          </TableCell>
                        )}
                      </React.Fragment>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginatedData && (
        <TablePagination
          rowsPerPageOptions={[10, 20, 30, 50, 100]}
          component="div"
          count={paginatedData.total}
          rowsPerPage={paginatedData.limit}
          page={paginatedData.page - 1}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          labelRowsPerPage={t("rowsPerPage")}
          disabled={isLoading}
        />
      )}
    </React.Fragment>
  );
}
